import { Dropdown } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
//@ts-ignore
import { HexColorPicker } from 'react-colorful';
import styled from 'styled-components';
//@ts-ignore
import useEyeDropper from 'use-eye-dropper';

import ColorDropletSvg from '../../../../assets/icons/ColorDropletSvg';
import { ColorPickerSvg } from '../../../../assets/icons/ColorPickerSvg';
import GoBackArrowSvg from '../../../../assets/icons/GoBackArrowSvg';
import theme from '../../../../assets/theme';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import {
  documentColorsSelector,
  useDesignerDataContext,
} from '../../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  TabState,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import { useOnOutsideClick } from '../../../../hooks/useOnOutsideClick';
import { MOBILE_TO_DESKTOP_BREAKPOINT_PX } from '../../../../utils/creator';
import { SLIDE_COLORS } from '../../../../utils/roundUtils';
import { Input } from '../../../_atoms/_Input';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { setMark, toggleMark } from '../../../SlideComponents/richTextEditorUtil';
import {
  OnElementsChangeProps,
  Slide,
  SlideElement,
} from '../../../SlideComponents/slide.types';
import {
  colorIsHexCode,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
  RICHTEXTEDITOR_ID_PREFIX,
} from '../../../SlideComponents/slideUtil';

const ColorPickerTab = ({
  onRoundChange,
  round,
  onElementsChange,
  selectedElements,
  tabState,
}: {
  onRoundChange: (round: Slide) => void;
  round?: Slide;
  droppedImageData?: { src: string; mimeType: string };
  selectedElements: SlideElement[] | null;
  onElementsChange: (prop: OnElementsChangeProps) => void;
  tabState: TabState;
}) => {
  const [customColor, setCustomeColor] = useState('');
  const { open, isSupported } = useEyeDropper();
  const onCloseDrawer = useDesignerUIContext(context => context.handleCloseDrawer);
  const isDesktop = useBreakPoint(MOBILE_TO_DESKTOP_BREAKPOINT_PX);
  const documentColors = useDesignerDataContext(documentColorsSelector);
  const handleSelectedTabChange = useDesignerDataContext(c => c.handleSelectedTabChange);
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const [showPicker, setShowPicker] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const richTextEditor = useDesignerUIContext(context => context.state.richTextEditor);
  const selectedRichTextEditors = useDesignerUIContext(
    c => c.state.selectedRichTextEditors,
  );
  const editingTextElementId = useDesignerUIContext(
    context => context.state.textElementIdBeingEdited,
  );
  useOnOutsideClick(dropdownRef, () => setShowPicker(false));

  const handleColorClick = useCallback(
    (color: string) => {
      if (selectedElements && selectedElements.length > 0) {
        if (selectedElements.length === 1 && richTextEditor && editingTextElementId) {
          toggleMark(richTextEditor, 'color', color);

          return;
        }
        // if you have selected multiple elements then we need to update all of them
        const updatedElements = [];
        for (const element of selectedElements) {
          if (isSlideElementText(element)) {
            const editor = selectedRichTextEditors.find(
              e => e.id === `${RICHTEXTEDITOR_ID_PREFIX}${element.id}`,
            );
            if (!editor) continue;
            // const isActive = isMarkActive(editor, formatType);
            setMark(editor, 'color', color);

            const updatedChildren = JSON.stringify(editor.children);
            const updatedElement: SlideElement = {
              ...element,
              textValue: updatedChildren,
            };
            updatedElements.push(updatedElement);
          }
          if (isSlideElementShape(element)) {
            const updatedElement: SlideElement = {
              ...element,
              colors: [color],
            };
            updatedElements.push(updatedElement);
          }
          if (isSlideElementSvg(element) && tabState.extraState) {
            const colorIndex = parseInt(tabState?.extraState);
            if (colorIndex !== undefined) {
              const newColors = [...element.colors];
              const existingColor = newColors?.[colorIndex];
              if (existingColor && existingColor !== color) {
                newColors[colorIndex] = color;
                const updatedElement: SlideElement = {
                  ...element,
                  colors: newColors,
                };
                updatedElements.push(updatedElement);
              }
            }
          }
        }

        onElementsChange({ updatedElements: updatedElements });
      } else if (tabState.extraState === 'backgroundSelector' && round) {
        onRoundChange({
          ...round,
          backgroundMedia: {
            fileId: color,
            fileMimeType: 'image/color',
          },
        });
      }
    },
    [
      editingTextElementId,
      onElementsChange,
      onRoundChange,
      richTextEditor,
      round,
      selectedElements,
      selectedRichTextEditors,
      tabState.extraState,
    ],
  );
  const pickColor = useCallback(() => {
    // Using async/await (can be used as a promise as-well)
    const openPicker = async () => {
      try {
        if (!isSupported()) return;
        const color = await open();
        setCustomeColor(color.sRGBHex);
        handleColorClick(color.sRGBHex);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        // Ensures component is still mounted
        // before calling setState
      }
    };
    openPicker();
  }, [handleColorClick, isSupported, open]);

  useEffect(() => {
    // if we are in the background selector then we don't want to change the tab
    if (tabState.extraState === 'backgroundSelector') return;

    if (!selectedElements || selectedElements?.length === 0) {
      handleSelectedTabChange({ tab: 'generators', subTab: undefined });
      if (!isDesktop) onCloseDrawer();
      return;
    }
    if (selectedElements.length > 0) {
      if (selectedElements.length === 1 && selectedElements[0].type === 'svg') {
        return;
      }
      if (
        selectedElements.some(
          selectedElement =>
            !isSlideElementText(selectedElement) && !isSlideElementShape(selectedElement),
        )
      ) {
        handleSelectedTabChange({ tab: 'text', subTab: undefined });
        return;
      }
    }
  }, [
    handleSelectedTabChange,
    isDesktop,
    onCloseDrawer,
    selectedElements,
    tabState.extraState,
  ]);
  const colorPickerOverlay = useCallback(
    () => (
      <HexColorPickerContainer ref={dropdownRef}>
        <HexColorPicker
          color={theme.colors.black}
          onChange={(color: string) => handleColorClick(color)}
        />
        <CustomColorContainer>
          <StyledInput
            placeholder="#F1975C"
            value={customColor}
            style={!isSupported() ? { width: '200px' } : {}}
            onChange={e => {
              setCustomeColor(e.target.value);
              if (colorIsHexCode(e.target.value)) handleColorClick(e.target.value);
            }}
          />
          {isSupported() && (
            <Button
              type="ghost"
              styles={{ padding: '10px', height: '42px', width: '42px' }}
              onClick={pickColor}
            >
              <IconWrapper height="20px" width="20px">
                <ColorDropletSvg />
              </IconWrapper>
            </Button>
          )}
        </CustomColorContainer>
      </HexColorPickerContainer>
    ),
    [customColor, handleColorClick, isSupported, pickColor],
  );

  const title = useMemo(() => {
    if (selectedElements && selectedElements.length > 0) return 'Color';
    if (tabState.extraState === 'backgroundSelector') return 'Background color';
    return 'Colors';
  }, [selectedElements, tabState.extraState]);
  const handleGoBack = useCallback(() => {
    dispatch({ type: DesignerUIStateActions.setSelectedTabGoBack });
  }, [dispatch]);

  return (
    <ColorPickerContainer>
      <OverTitle onClick={handleGoBack}>
        <IconWrapper>
          <GoBackArrowSvg />
        </IconWrapper>
        {title}
      </OverTitle>
      <ColorTitle>Document colors </ColorTitle>

      <ColorsContainer>
        <Dropdown overlay={colorPickerOverlay} visible={showPicker}>
          <IconWrapper
            height="45px"
            width="45px"
            onClick={() => setShowPicker(!showPicker)}
          >
            <ColorPickerSvg />
          </IconWrapper>
        </Dropdown>
        {documentColors.map((color, i) => (
          <ColorItem key={i} color={color} onClick={() => handleColorClick(color)} />
        ))}
      </ColorsContainer>
      <ColorTitle>Default colors </ColorTitle>
      <ColorsContainer>
        {SLIDE_COLORS.map((color, i) => (
          <ColorItem key={i} color={color} onClick={() => handleColorClick(color)} />
        ))}
      </ColorsContainer>
    </ColorPickerContainer>
  );
};

const ColorPickerContainer = styled.div`
  padding: 20px;
  height: 100%;
  position: relative;
  overflow-y: auto;
`;
const ColorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding-bottom: 20px;
`;
const CustomColorContainer = styled.div`
  display: flex;
  justify-content: space-between;
  svg {
    margin: 0;
  }
`;

const ColorItem = styled.div<{ color: string }>`
  width: 45px;
  height: 45px;
  background-color: ${({ color }) => color};
  cursor: pointer;
  border-radius: 50%;
  border: 1px solid ${theme.colors.greyLight};
`;
const StyledInput = styled(Input)`
  width: 150px;
  margin-bottom: 10px;
  input {
    text-align: center;
  }
`;

const ColorTitle = styled.span`
  color: ${theme.colors.greyDark};
  display: flex;
  gap: 5px;
`;

const HexColorPickerContainer = styled.div`
  background-color: ${theme.colors.white};
  padding: 10px;
  border-radius: 10px;
  .react-colorful__hue.react-colorful__last-control {
    margin: 10px 0 !important;
    border-radius: 5px;
  }
  .react-colorful__saturation {
    border-radius: 5px !important;
  }
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
`;
const OverTitle = styled.h2`
  display: flex;

  gap: 10px;
  color: ${theme.colors.greyDark};
  cursor: pointer;
`;

export default ColorPickerTab;
